import React, { ReactNode, useContext, useMemo } from 'react';
import useClientDetails from '../../../utils/hooks/use_client_details';

interface ClientDataContextTypes {
  isAuthEnabled: boolean;
  isAdminDashboardEnabled: boolean;
  areInvitesEnabled: boolean;
  areAssaysEnabled: boolean;
  isAssaysV2Enabled: boolean;
  useArrowLogFormat: boolean;
  isJupyterEnabled: boolean;
  isOrchestrationEnabled: boolean;
  isEdgeEnabled: boolean;
  isEdgePublishingEnabled: boolean;
  clientName: string;
  error: string | null;
}

const ClientDataContext = React.createContext<ClientDataContextTypes>({
  isAuthEnabled: false,
  isAdminDashboardEnabled: false,
  areInvitesEnabled: false,
  areAssaysEnabled: false,
  isAssaysV2Enabled: false,
  useArrowLogFormat: false,
  isJupyterEnabled: false,
  isOrchestrationEnabled: false,
  isEdgeEnabled: false,
  isEdgePublishingEnabled: false,
  clientName: 'Wallaroo',
  error: null,
});

interface Props {
  children?: ReactNode | null;
}

export const ClientDataProvider = ({ children = null }: Props) => {
  const { clientDetails, error } = useClientDetails();
  const isAuthEnabled = clientDetails?.is_auth_enabled || false;
  const areInvitesEnabled = clientDetails?.are_invites_enabled || false;
  // Controls whether the Insights tab is shown at all
  const areAssaysEnabled = clientDetails?.are_assays_enabled || false;
  // Controls whether the logic used is v1 (model insights) or v2 (wallsvc)
  const isAssaysV2Enabled = clientDetails?.is_assays_v2_enabled || false;
  const useArrowLogFormat = clientDetails?.use_arrow || false;
  const isJupyterEnabled = clientDetails?.is_jupyter_enabled || false;
  const areEventsEnabled = clientDetails?.are_events_enabled || false;
  const areExternalInferenceURLsEnabled =
    clientDetails?.are_external_inference_urls_enabled || false;
  const isOrchestrationEnabled = clientDetails?.is_orchestration_enabled || false;
  const isEdgeEnabled = clientDetails?.is_edge_enabled || false;
  const isEdgePublishingEnabled = clientDetails?.is_edge_publishing_enabled || false;
  const isAdminDashboardEnabled = clientDetails?.is_admin_dashboard_enabled || false;
  const clientName = clientDetails?.name || 'Wallaroo';

  const clientDataContextValue = useMemo(
    () => ({
      isAuthEnabled,
      isAdminDashboardEnabled,
      areInvitesEnabled,
      areAssaysEnabled,
      isAssaysV2Enabled,
      useArrowLogFormat,
      isJupyterEnabled,
      isOrchestrationEnabled,
      isEdgeEnabled,
      isEdgePublishingEnabled,
      areEventsEnabled,
      areExternalInferenceURLsEnabled,
      clientName,
      error,
    }),
    [
      isAuthEnabled,
      isAdminDashboardEnabled,
      areInvitesEnabled,
      areAssaysEnabled,
      isAssaysV2Enabled,
      useArrowLogFormat,
      isJupyterEnabled,
      isOrchestrationEnabled,
      isEdgeEnabled,
      isEdgePublishingEnabled,
      areEventsEnabled,
      areExternalInferenceURLsEnabled,
      clientName,
      error,
    ]
  );

  return (
    <ClientDataContext.Provider value={clientDataContextValue}>
      {children}
    </ClientDataContext.Provider>
  );
};

export const useClientContext = () => useContext(ClientDataContext);

export default useClientContext;
